import { buildCognitoSignoutUrl } from './oidcConfig';
import { useMocks } from '@config/env';

/**
 * Ends the current session and returns the user to the login screen.
 *
 * - **Cognito:** clears the `oidc.*` client state from BOTH web stores — the signed-in session lives in
 *   `localStorage` so it survives a browser restart, while the transient redirect state stays in
 *   `sessionStorage` — then navigates to the Cognito hosted-UI `/logout`, which redirects back to the
 *   app where `AuthGuard` sends the now-unauthenticated visitor to sign-in. Missing either store would
 *   leave a persisted session behind and make "sign out" a lie.
 * - **Mock mode:** no Cognito session exists, so it simply reloads the app root.
 *
 * Deliberately does NOT call `auth.removeUser()` — that flips the OIDC context and races the logout
 * navigation. Kept free of `useAuth()` so it is safe to call from the Not-Authorized screen, which
 * renders before the admin-group gate passes.
 */
export function signOutToLogin(): void {
  if (useMocks) {
    window.location.replace('/');
    return;
  }

  clearOidcKeys(window.localStorage);
  clearOidcKeys(window.sessionStorage);
  window.location.replace(buildCognitoSignoutUrl());
}

/** Removes every `oidc.*` entry from one store. Collects first: removing shifts the key indices. */
function clearOidcKeys(store: Storage): void {
  const doomed: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key?.startsWith('oidc.')) doomed.push(key);
  }
  for (const key of doomed) store.removeItem(key);
}
